import { marked } from 'marked';
import { Options as ReactMarkdownProps } from 'react-markdown';

interface ParserResult {
  type: 'simple' | 'sectioned';
  content: Content[];
}

export interface Content {
  heading?: string;
  body: string;
}

interface SectionToken {
  type: string;
  raw: string;
  depth: number;
  text: string;
  tokens: marked.Token[];
}

/**
 * See: https://marked.js.org/using_pro#extensions
 */
const SectionTokenizerExtension: marked.TokenizerExtension = {
  level: 'block',
  name: 'SectionTokenizerExtensison',
  tokenizer(this, src) {
    // Previously used their lheading rule directly, but was changed in 4.2.3. Now hardcoding the old one.
    // Taken from: https://github.com/markedjs/marked/pull/2655/files#diff-1de7312ecbb8138fea99c0f206d07d4e534c7a513f615d3a54cc66bc9c702c67L30
    const rule = /^([^\n]+)\n {0,3}(=+|-+) *(?:\n+|$)/;
    const match = rule.exec(src);
    if (!match) {
      return;
    }

    const [raw, text, headingChars] = match;
    // Headings considered sections of the accordion need to have a "=" char
    if (headingChars.charAt(0) === '=') {
      const token = {
        raw,
        text,
        type: 'section',
        depth: 1,
        tokens: [],
      };
      this.lexer.inline(token.text, token.tokens);
      return token;
    }
  },
};

marked.use({ extensions: [SectionTokenizerExtension] });

/**
 * parseAssignmentMarkdown implements the parsing logic for the markdown that's passed into it.
 * @param markdown parsed to capture section tokens.
 */
export function parseMarkdown(markdown: string): ParserResult {
  const tokens = marked.lexer(markdown.trimStart()) as (marked.Token | SectionToken)[];
  const isSectioned = tokens.some(token => token.type === 'section');

  if (isSectioned) {
    return {
      type: 'sectioned',
      content: tokens.reduce<Content[]>((content, token) => {
        if (token.type === 'section') {
          content.push({
            heading: token.text,
            body: '',
          });
        } else {
          if (!content.length) {
            content.push({
              heading: 'Introduction',
              body: '',
            });
          }

          content[content.length - 1].body += token.raw + '\n';
        }

        return content;
      }, []),
    };
  }

  return {
    type: 'simple',
    content: [{ body: tokens.map(token => token.raw + '\n').join('') }],
  };
}

export const defaultComponents: ReactMarkdownProps['components'] = {
  a: ({ node, children, ...props }) => {
    // By default we want to open external links in a new tab
    if (props.href?.startsWith('http')) {
      props.rel ??= 'noopener noreferrer';
      props.target ??= '_blank';
    }

    // Some clients (e.g. datadog) pass classes & id to their anchors for custom functionality
    return <a {...props}>{children}</a>;
  },
};
